import { createInventoryItem, resolveItemTypeToMeta } from "game-common/item/item"
import { Inventory, TileSize } from "game-common/models"
import { forestGrassTile } from "game-common/room/ember/grass_art"
import { BLEND_MODES, Circle, Container, Graphics, InteractionEvent, InteractionManager, LINE_CAP, LINE_JOIN, Point, Sprite, Text, Texture } from "pixi.js"
import { contentSheetResource } from "../content_textures"
import { BuildPlacementFeedback } from "../effects/build_placement_feedback"
import { getRendererApp } from "../renderer_app"
import { GuiManager } from "../gui_manager"
import { TextRenderer } from "../text/text_renderer"
import { SCALER } from "../../../../client_util"
import { chamfer, drawBoard, GOLD, INK, MUTED, PLOT_FACE, PLOT_RIM, RIM, RIM_LIGHT, TIMBER_EDGE } from "./build_board"
import { BuildingPrintCountdown } from "./building_print_countdown"
import { diamond, filledSocket, glowDisc, shineBand, spark, sparkBurst, REWARD_GOLD, REWARD_GOLD_HOT } from "./chapter_reward_art"
import { easeInCubic, easeInOutCubic, easeOutBack, easeOutCubic, lerp, lerpColor, quadPoint, span, stampPose } from "./chapter_reward_motion"
import { EquippedWidget, SLOT, SLOT_PITCH, TRAY_HEIGHT, TRAY_PAD } from "./equipped"
import { UiButton } from "./ui_button"
import { getUiMetrics } from "./ui_metrics"

// The building helper: a little diorama of the camp in which a pointer readies a wall, drags a row
// and lets go, and the row goes up. It is a lesson the player watches rather than a form they read,
// so it is drawn in the camp HUD's own materials (build_board's timber and bronze, the clues' gold
// spark, the chapter checklist's diamonds and stamp) rather than as a panel of numbered sentences.
//
// Everything inside the frame is the REAL thing: the ready bar is the HUD's EquippedWidget, the
// tiles come from the content atlases, and the drag, the counting halo and the build are drawn by
// the game's own BuildPlacementFeedback and BuildingPrintCountdown -- so what the demo shows is
// exactly what the player will see when they try it. Only the chrome around it (step track,
// pointer, the closing flourish) belongs to the demo.

// The board, and the diorama inside it. CELL must equal TileSize: the placement feedback draws in
// world tiles, and the scene is laid out on the same grid at 1:1.
const CELL = 32
const COLS = 9, ROWS = 5
const SCENE_W = COLS * CELL, SCENE_H = ROWS * CELL
const WIDTH = SCENE_W + 44
const SCENE_X = (WIDTH - SCENE_W) / 2
const SCENE_Y = 92
const CAPTION_Y = SCENE_Y + SCENE_H + 13
const CAPTION_H = 36
const HEIGHT = CAPTION_Y + CAPTION_H + 10
// The wall goes up on the FACE row, three sections from WALL_COL, each top one row above its face.
const WALL_COL = 3, FACE_ROW = 2, WALL_SECTIONS = 3
const WALL_X = SCENE_X + WALL_COL * CELL, WALL_Y = SCENE_Y + (FACE_ROW - 1) * CELL
const WALL_W = WALL_SECTIONS * CELL, WALL_H = 2 * CELL

// Header: the clues' gold spark beside a letterspaced overline, then the title.
const PAD_X = SCENE_X
const OVERLINE_Y = 15
const TITLE_Y = 27
const LETTER_SPACING = 1.6
// The step track, between the title and the scene.
const TRACK_Y = 72
const NODE_R = 6.5
const NODE_LABEL_GAP = 6
const LINK_GAP = 8

// The close X: bare, like the clue notes' (clue_callout.ts), so the loudest thing on the board is
// never the way out of it.
const CLOSE_ARM = 3.75
const CLOSE_HIT = 12

const REPLAY_W = 84, REPLAY_H = 26

// Arrival and departure: set down onto the screen, lifted away -- never a dialog popping.
const ENTER_MS = 320, ENTER_RISE = 12
const LEAVE_MS = 170, LEAVE_DROP = 6

// ---- The timeline ------------------------------------------------------------------------------
//
// One play-through, posed from its own elapsed milliseconds and then held on the last frame until
// Replay. Everything that moves inside it is a pure function of that clock, so any instant can be
// posed exactly; only effects that run on after an event (sparks, the settle) use the monotonic
// frame clock, which keeps running while the play-through holds.

export const BUILDING_DEMO_MS = 8000
const SELECT_AT = 1000 // the pointer clicks the wall on the ready bar
const DRAG_FROM = 1900 // ...and heads for the ground
const PRESS_AT = 2550
const DRAG_START = 2650, DRAG_END = 3750
const RELEASE_AT = 3950
const PRINT_MS = 2700
const BUILT_AT = RELEASE_AT + PRINT_MS
const STAMP_AT = BUILT_AT + 250
const DONE_AT = BUILT_AT + 450
const REPLAY_AT = 7400
const RIPPLES = [SELECT_AT, PRESS_AT, RELEASE_AT]
const RIPPLE_MS = 460

// The step track: when each step lights, when it is checked off, and when the link to the next
// one fills.
const STEP_LABELS = ["PICK", "DRAG", "BUILD"]
const STEP_LIT_AT = [0, DRAG_FROM, RELEASE_AT + 420]
const STEP_DONE_AT = [SELECT_AT, RELEASE_AT, BUILT_AT]
const LINK_FILL = [[SELECT_AT + 120, DRAG_FROM], [RELEASE_AT + 60, RELEASE_AT + 420]]
const STEP_LIGHT_MS = 260

const CAPTIONS = [
    "Pick the wall from your ready bar.",
    "Drag across the ground to highlight a row.",
    "Let go and wait. The whole row builds at once.",
]
const DONE_CAPTION = "Your wall is up! Now raise your own."
const CAPTION_IN_MS = 240

/** Where the demonstration is at `elapsedMs` into one play-through. */
export const buildingTutorialPose = (elapsedMs: number) => {
    const t = Math.min(BUILDING_DEMO_MS, Math.max(0, elapsedMs))
    const drag = easeInOutCubic(span(t, DRAG_START, DRAG_END))
    return {
        step: t < DRAG_FROM ? 0 : t < RELEASE_AT ? 1 : 2,
        selected: t < SELECT_AT ? 0 : 1,
        drag,
        // A cell lights when the POINTER crosses into it (halfway between two cell centres), not on
        // a timer, so the highlight is seen to follow the hand.
        tiles: t < PRESS_AT ? 0 : 1 + (drag >= 0.25 ? 1 : 0) + (drag >= 0.75 ? 1 : 0),
        released: t >= RELEASE_AT,
        built: t >= BUILT_AT,
        done: t >= DONE_AT,
    }
}

type Pt = { x: number; y: number }
export interface CursorStops { rest: Pt; slot: Pt; start: Pt; end: Pt }

// A hand moves in arcs, not along a ruler.
const glide = (from: Pt, to: Pt, k: number) =>
    quadPoint(from, { x: (from.x + to.x) / 2, y: Math.min(from.y, to.y) - 18 }, to, easeInOutCubic(k))

/** The pointer: where it is, whether it is held down, how visible it is. */
export const buildingCursorPose = (elapsedMs: number, at: CursorStops) => {
    const t = Math.min(BUILDING_DEMO_MS, Math.max(0, elapsedMs))
    let p: Pt
    if (t < DRAG_FROM) p = glide(at.rest, at.slot, span(t, 150, 900))
    else if (t < PRESS_AT) p = glide(at.slot, at.start, span(t, DRAG_FROM, PRESS_AT - 80))
    else if (t < RELEASE_AT) p = { x: lerp(at.start.x, at.end.x, buildingTutorialPose(t).drag), y: at.start.y }
    else {
        // Let go, then step back out of the way of the dial and the build.
        const k = easeInOutCubic(span(t, RELEASE_AT + 120, RELEASE_AT + 700))
        p = { x: at.end.x + 24 * k, y: at.end.y + 30 * k }
    }
    return {
        ...p,
        pressed: (t >= SELECT_AT && t < SELECT_AT + 170) || (t >= PRESS_AT && t < RELEASE_AT),
        alpha: Math.min(easeOutCubic(span(t, 0, 250)), 1 - easeInCubic(span(t, RELEASE_AT + 250, RELEASE_AT + 750))),
    }
}

/** The plaque that slams in over the finished wall, `ms` after it starts. Settled within 1.1s. */
export const raisedStampPose = (ms: number) => ({
    alpha: ms < 0 ? 0 : easeOutCubic(span(ms, 0, 120)),
    scale: ms < 0 ? 0 : lerp(1.9, 1, easeOutBack(span(ms, 0, 320), 2.2)),
    checkDraw: easeOutCubic(span(ms, 90, 380)),
    burstT: span(ms, 0, 620),
    shineT: ms < 0 ? -1 : easeInOutCubic(span(ms, 340, 1000)),
})

const cursorArrow = [0, 0, 0, 13.5, 3.3, 10.4, 5.7, 15.6, 7.9, 14.6, 5.6, 9.6, 9.9, 9.6]

/** Real game controls and atlas tiles in an isolated scene. All control callbacks are local. */
export class BuildingTutorial extends Container {
    private scene = new Container()
    private frame = new Graphics()
    private trackGlow = new Graphics()
    private track = new Graphics()
    private trackLabels: Text[] = []
    private trackX: { node: number; label: number; end: number }[] = []
    private fx = new Graphics()
    private stamp = new Container()
    private stampPlate = new Graphics()
    private stampShine = new Graphics()
    // The plaque's burst, squashed flat so it fans out along the free row instead of up into the
    // step track.
    private stampBurst = new Graphics()
    private stampSize = { w: 0, h: 24 }
    private pointer = new Graphics()
    private closeX = new Graphics()
    private readyBar: EquippedWidget
    private countdown: BuildingPrintCountdown
    private feedback: BuildPlacementFeedback
    private inventory: Inventory = { items: ["axe", "wall-wood"].map((type: any, i) => ({
        ...createInventoryItem(resolveItemTypeToMeta(type), { id: `tutorial-${type}`, quantity: i === 1 ? 3 : 1 }),
        readied: true, active: i === 0,
    })), abilities: {}, lastUpdateTs: 0 }
    private art: { sprite: Sprite; sheet: string; tile: number; parent?: number }[] = []
    private replayButton: UiButton
    private caption: Text
    private captionShownAt = 0
    private elapsed = 0
    // The frame clock: runs on while a play-through holds on its last frame.
    private clock = 0
    private leavingAt?: number
    private closeHovered = false
    // What the play-through has already told the placement feedback, so each event fires once.
    private played = { tiles: 0, released: false, built: false }
    private hovered = false
    private selected = -1
    private manualPosition?: Point
    private drag?: { id: number; offset: Point; interaction?: InteractionManager }
    private titleHandle = new Graphics()

    constructor(private hover: (over: boolean) => void, private dismiss: () => void) {
        super()
        const board = new Graphics(); drawBoard(board, WIDTH, HEIGHT); this.addChild(board)
        this.drawHeader()
        this.trackGlow.blendMode = BLEND_MODES.ADD
        this.addChild(this.trackGlow)
        this.addChild(this.track)
        this.buildTrackLabels()

        // The diorama. Sortable, so the placement feedback and the print dial keep their world
        // stacking (dial over feedback over wall ghosts) as they do on the real map.
        this.scene.position.set(SCENE_X, SCENE_Y)
        this.scene.sortableChildren = true
        this.addChild(this.scene)
        for (let y = 0; y < ROWS; y++) for (let x = 0; x < COLS; x++) {
            this.addTile("forest-grass", forestGrassTile(x, y), x, y)
        }
        // Three wall sections on one row, each with its matching top directly above its face.
        ;[[257, 273], [257, 273], [258, 274]].forEach(([top, face], x) => {
            this.addTile("fence_medieval", top, WALL_COL + x, FACE_ROW - 1, x)
            this.addTile("fence_medieval", face, WALL_COL + x, FACE_ROW, x)
        })
        this.feedback = new BuildPlacementFeedback(() => this.scene, () => this.clock)
        this.countdown = new BuildingPrintCountdown(() => this.scene)
        this.countdown.scale.set(2, 2 * SCALER)
        // The dial's root sits half a tile into its group's anchor; put it on the middle section.
        const dialX = (WALL_COL + 1.5) * CELL, dialY = (FACE_ROW + 0.5) * CELL
        this.countdown.position.set(dialX - TileSize, dialY - TileSize * SCALER)
        this.scene.addChild(this.countdown)
        this.drawFrame()
        this.addChild(this.frame)

        // The ready bar where the HUD keeps it: docked in the bottom-left corner of the view.
        this.readyBar = new EquippedWidget({} as GuiManager)
        this.readyBar.position.set(SCENE_X + 6, SCENE_Y + SCENE_H - TRAY_HEIGHT - 6)
        this.addChild(this.readyBar)

        this.fx.blendMode = BLEND_MODES.ADD
        this.addChild(this.fx)
        this.buildStamp()
        this.addChild(this.pointer)

        this.caption = new TextRenderer().fontSize(13).color(INK).strokeThickness(0).render("") as Text
        this.caption.style.wordWrap = true; this.caption.style.wordWrapWidth = SCENE_W
        this.caption.position.set(PAD_X, CAPTION_Y); this.addChild(this.caption)
        this.replayButton = new UiButton({ label: "Replay", width: REPLAY_W, height: REPLAY_H, size: "small",
            theme: "ember", shape: "chamfer", onClick: this.replay })
        this.replayButton.position.set(WIDTH - PAD_X - REPLAY_W, CAPTION_Y + (CAPTION_H - REPLAY_H) / 2 - 2)
        this.addChild(this.replayButton)

        this.titleHandle.beginFill(0xffffff, 0.001).drawRect(8, 6, WIDTH - 48, TRACK_Y - 16).endFill()
        this.titleHandle.interactive = true
        this.titleHandle.cursor = "grab"
        this.titleHandle.on("pointerdown", this.startDrag)
        this.titleHandle.on("pointermove", this.moveDrag)
        this.titleHandle.on("pointerup", this.endDrag)
        this.titleHandle.on("pointerupoutside", this.endDrag)
        this.titleHandle.on("pointercancel", this.endDrag)
        this.addChild(this.titleHandle)

        this.closeX.position.set(WIDTH - 22, 22)
        this.closeX.interactive = true
        this.closeX.cursor = "pointer"
        this.closeX.hitArea = new Circle(0, 0, CLOSE_HIT)
        this.closeX.on("pointerover", () => { this.closeHovered = true; this.drawClose() })
        this.closeX.on("pointerout", () => { this.closeHovered = false; this.drawClose() })
        this.closeX.on("pointertap", (event: InteractionEvent) => { event.stopPropagation(); this.leave() })
        this.drawClose()
        this.addChild(this.closeX)

        this.interactive = true
        this.on("mouseover", () => { this.hovered = true; this.hover(true) })
        this.on("mouseout", () => { if (!this.drag) { this.hovered = false; this.hover(false) } })
        this.on("pointerdown", event => event.stopPropagation())
        this.animate(0)
    }

    // ---- Construction ---------------------------------------------------------------------------

    private drawHeader = () => {
        const sparkG = new Graphics()
        sparkG.beginFill(GOLD, 1); spark(sparkG, PAD_X + 3.5, OVERLINE_Y + 5.5, 3.5); sparkG.endFill()
        this.addChild(sparkG)
        const overline = new TextRenderer().role("compactCaption").bold().strokeThickness(0).color(RIM_LIGHT)
            .render("BUILDING") as Text
        overline.style.letterSpacing = LETTER_SPACING
        overline.position.set(PAD_X + 13, OVERLINE_Y)
        this.addChild(overline)
        const title = new TextRenderer().fontSize(18).bold().strokeThickness(0).color(INK).render("Raise a wall") as Text
        title.position.set(PAD_X, TITLE_Y)
        this.addChild(title)
    }

    private buildTrackLabels = () => {
        const widths = STEP_LABELS.map(label => {
            const text = new TextRenderer().fontSize(9).bold().strokeThickness(0).render(label) as Text
            text.style.letterSpacing = 1.4
            text.anchor.set(0, 0.5)
            text.y = TRACK_Y
            this.trackLabels.push(text)
            this.addChild(text)
            // A headless canvas measures nothing; never let the track collapse onto itself.
            // pixi's letterSpacing adds a trailing advance after the last glyph -- take it back off.
            let width = 0
            try { text.updateText(true); width = text.width - 1.4 } catch { /* measured below */ }
            return Math.max(width, label.length * 6.5)
        })
        // Diamond, its label, then a link reaching to the next diamond; the links share what is left.
        const fixed = widths.reduce((sum, w) => sum + NODE_R * 2 + NODE_LABEL_GAP + w, 0)
        const link = Math.max(12, (SCENE_W - fixed - LINK_GAP * 4) / 2)
        let x = PAD_X
        widths.forEach((w, i) => {
            const node = x + NODE_R
            const label = x + NODE_R * 2 + NODE_LABEL_GAP
            this.trackLabels[i].x = label
            x = label + w + LINK_GAP
            this.trackX.push({ node, label, end: x })
            x += link + LINK_GAP
        })
    }

    private addTile = (sheet: string, tile: number, x: number, y: number, parent?: number) => {
        const sprite = new Sprite(Texture.EMPTY)
        sprite.position.set(x * CELL, y * CELL)
        sprite.width = CELL; sprite.height = CELL
        this.scene.addChild(sprite)
        this.art.push({ sprite, sheet, tile, parent })
    }

    /**
     * The diorama is set INTO the board: a timber casing with chamfered inner corners, shadowed
     * along the top where the board overhangs it and lit along the bottom lip, and the grass
     * darkening toward the edges so the eye stays on the middle where the wall goes up.
     */
    private drawFrame = () => {
        const g = this.frame
        const x = SCENE_X, y = SCENE_Y, w = SCENE_W, h = SCENE_H
        // The vignette: stacked frames with ever-smaller holes, equal alpha each, so the falloff is
        // their accumulation -- darkest at the casing, gone a few cells in.
        for (let i = 1; i <= 8; i++) {
            const d = i * 2.5
            g.beginFill(0x000000, 0.045)
            g.drawRect(x, y, w, h)
            g.beginHole()
            g.drawRect(x + d, y + d, w - d * 2, h - d * 2)
            g.endHole()
            g.endFill()
        }
        // Inner shadow under the top lip.
        for (let i = 0; i < 4; i++) {
            g.lineStyle(1, 0x000000, 0.34 - i * 0.075).moveTo(x, y + 0.5 + i).lineTo(x + w, y + 0.5 + i)
        }
        // The casing: a band with chamfered inner corners, covering the scene's own square corners.
        g.lineStyle(0)
        g.beginFill(TIMBER_EDGE, 1)
        g.drawRect(x - 3, y - 3, w + 6, h + 6)
        g.beginHole()
        chamfer(g, x, y, w, h, 5)
        g.endHole()
        g.endFill()
        g.lineStyle({ width: 1, color: RIM, alpha: 0.75, join: LINE_JOIN.MITER })
        chamfer(g, x - 0.5, y - 0.5, w + 1, h + 1, 5.5)
        g.lineStyle(1, RIM_LIGHT, 0.22).moveTo(x + 6, y + h + 1.5).lineTo(x + w - 6, y + h + 1.5)
    }

    private buildStamp = () => {
        const text = new TextRenderer().fontSize(11).bold().strokeThickness(0).render("WALL RAISED") as Text
        text.style.letterSpacing = LETTER_SPACING
        text.anchor.set(0, 0.5)
        let width = 0
        try { text.updateText(true); width = text.width - LETTER_SPACING } catch { /* fallback below */ }
        width = Math.max(width, 80)
        const w = Math.round(width + 46), h = this.stampSize.h
        this.stampSize.w = w
        text.position.set(-w / 2 + 31, 0.5)
        text.tint = REWARD_GOLD
        // Centred over the wall, in the free row above its tops.
        this.stamp.position.set(WALL_X + WALL_W / 2, SCENE_Y + CELL / 2)
        this.stampShine.blendMode = BLEND_MODES.ADD
        this.stamp.addChild(this.stampPlate, text, this.stampShine)
        this.stamp.visible = false
        this.stampBurst.blendMode = BLEND_MODES.ADD
        this.stampBurst.position.copyFrom(this.stamp.position)
        this.stampBurst.scale.set(1, 0.4)
        this.addChild(this.stampBurst, this.stamp)
    }

    private drawClose = () => {
        const g = this.closeX.clear()
        g.lineStyle({ width: 1.8, color: this.closeHovered ? GOLD : MUTED, alpha: this.closeHovered ? 1 : 0.85,
            cap: LINE_CAP.ROUND })
        g.moveTo(-CLOSE_ARM, -CLOSE_ARM).lineTo(CLOSE_ARM, CLOSE_ARM)
        g.moveTo(CLOSE_ARM, -CLOSE_ARM).lineTo(-CLOSE_ARM, CLOSE_ARM)
        g.lineStyle(0)
    }

    // ---- Window -------------------------------------------------------------------------------

    private replay = () => {
        this.elapsed = 0
        this.feedback.clear()
        this.played = { tiles: 0, released: false, built: false }
        this.animate(0)
    }

    private leave = () => {
        if (this.leavingAt === undefined) this.leavingAt = this.clock
    }

    private startDrag = (event: InteractionEvent) => {
        if (this.drag || !this.parent || (event.data.button ?? 0) !== 0) return
        event.stopPropagation()
        const at = this.parent.toLocal(event.data.global)
        let interaction: InteractionManager | undefined
        try { interaction = getRendererApp().renderer.plugins.interaction } catch { /* Detached previews can use the title's events. */ }
        this.drag = { id: event.data.pointerId, offset: new Point(at.x - this.x, at.y - this.y), interaction }
        this.manualPosition = this.position.clone()
        this.titleHandle.cursor = "grabbing"
        this.hovered = true; this.hover(true)
        interaction?.on("pointermove", this.moveDrag)
        interaction?.on("pointerup", this.endDrag)
        interaction?.on("pointerupoutside", this.endDrag)
        interaction?.on("pointercancel", this.endDrag)
    }

    private moveDrag = (event: InteractionEvent) => {
        if (!this.drag || !this.parent || event.data.pointerId !== this.drag.id) return
        event.stopPropagation()
        const at = this.parent.toLocal(event.data.global)
        this.manualPosition.set(at.x - this.drag.offset.x, at.y - this.drag.offset.y)
        this.placeWindow()
    }

    private endDrag = (event?: InteractionEvent) => {
        if (!this.drag || event && event.data.pointerId !== this.drag.id) return
        const interaction = this.drag.interaction
        interaction?.off("pointermove", this.moveDrag)
        interaction?.off("pointerup", this.endDrag)
        interaction?.off("pointerupoutside", this.endDrag)
        interaction?.off("pointercancel", this.endDrag)
        this.drag = undefined
        this.titleHandle.cursor = "grab"
        this.hovered = !!event?.data?.global && this.getBounds().contains(event.data.global.x, event.data.global.y)
        this.hover(this.hovered)
    }

    private placeWindow = () => {
        const safe = getUiMetrics().safeViewport
        const scale = Math.min(1, Math.max(0.5, (safe.width - 24) / WIDTH), Math.max(0.5, (safe.height - 100) / HEIGHT))
        this.scale.set(scale)
        const desired = this.manualPosition || new Point(safe.right - WIDTH * scale - 12,
            safe.top + Math.max(60, (safe.height - HEIGHT * scale) / 2))
        const x = Math.max(safe.left, Math.min(desired.x, Math.max(safe.left, safe.right - WIDTH * scale)))
        const y = Math.max(safe.top, Math.min(desired.y, Math.max(safe.top, safe.bottom - HEIGHT * scale)))
        this.position.set(x, y)
        this.manualPosition?.set(x, y)
    }

    // ---- The frame ----------------------------------------------------------------------------

    animate = (deltaMs: number) => {
        if (this.destroyed) return
        const step = Math.max(0, deltaMs || 0)
        this.clock += step
        // Arrival and departure ride on the PIVOT, so the position dragging reads stays clean.
        if (this.leavingAt !== undefined) {
            const k = easeInCubic(span(this.clock - this.leavingAt, 0, LEAVE_MS))
            this.alpha = 1 - k
            this.pivot.y = -LEAVE_DROP * k
            if (k >= 1) { this.dismiss(); return }
        } else {
            const k = span(this.clock, 0, ENTER_MS)
            this.alpha = easeOutCubic(Math.min(1, k * 1.6))
            this.pivot.y = -ENTER_RISE * (1 - easeOutBack(k, 1.4))
        }
        this.elapsed = Math.min(BUILDING_DEMO_MS, this.elapsed + step)
        const t = this.elapsed
        const pose = buildingTutorialPose(t)

        if (this.selected !== pose.selected) {
            this.selected = pose.selected
            this.inventory.items.forEach((item, i) => item.active = i === pose.selected)
            this.readyBar.update(this.inventory, () => {})
        }
        this.readyBar.animate(deltaMs)

        this.art.forEach(({ sprite, sheet, tile, parent }) => {
            const texture = contentSheetResource(sheet)?.textures?.[`${sheet}-${tile}`] || Texture.EMPTY
            if (sprite.texture !== texture) { sprite.texture = texture; sprite.width = CELL; sprite.height = CELL }
            if (parent === undefined) return
            // The real print queue shows ghost walls until its countdown commits the batch.
            sprite.visible = parent < pose.tiles
            sprite.alpha = pose.built ? 1 : 0.4
        })
        this.playFeedback(pose)

        if (pose.released && !pose.built) {
            this.countdown.setGroups([{ id: 1, anchor: { x: 0, y: 0 }, startedAt: RELEASE_AT, duration: PRINT_MS,
                count: WALL_SECTIONS, onCancel: () => {} }], t)
        } else this.countdown.setGroups([], t)

        this.drawTrack(t)
        this.drawFx(t)
        this.drawStamp(t)
        this.drawPointer(t)
        this.updateCaption(pose)
        this.replayButton.visible = t >= REPLAY_AT
        this.replayButton.alpha = easeOutCubic(span(t, REPLAY_AT, REPLAY_AT + 300))
        this.placeWindow()
    }

    /** Tell the game's own placement feedback what a real drag would have, once per event. */
    private playFeedback = (pose: ReturnType<typeof buildingTutorialPose>) => {
        const key = (i: number) => `${WALL_COL + i},${FACE_ROW}`
        const cell = (i: number) => ({ x: WALL_COL + i, y: FACE_ROW })
        while (this.played.tiles < pose.tiles && !pose.released) {
            this.feedback.add(key(this.played.tiles), cell(this.played.tiles))
            this.played.tiles++
        }
        if (pose.tiles > 0 && !pose.released) this.feedback.setLineEnd(cell(pose.tiles - 1), "horizontal")
        if (pose.released && !this.played.released) {
            this.played.released = true
            this.feedback.clearLineEnd()
            for (let i = 0; i < WALL_SECTIONS; i++) {
                if (i >= this.played.tiles) { this.feedback.add(key(i), cell(i), 0, false); this.played.tiles++ }
                this.feedback.setState(key(i), "valid")
                this.feedback.setCounting(key(i), this.clock, 1)
            }
        }
        if (pose.built && !this.played.built) {
            this.played.built = true
            for (let i = 0; i < WALL_SECTIONS; i++) {
                this.feedback.remove(key(i))
                this.feedback.spark(cell(i), "built")
            }
            this.feedback.settle(this.art.filter(tile => tile.parent !== undefined).map(tile => tile.sprite))
        }
        this.feedback.update()
    }

    /** PICK -> DRAG -> BUILD: each lights when it is the step, and is stamped when it is done. */
    private drawTrack = (t: number) => {
        const g = this.track.clear()
        const glow = this.trackGlow.clear()
        // The links first, so the diamonds sit over their ends.
        LINK_FILL.forEach(([from, to], i) => {
            const x0 = this.trackX[i].end, x1 = this.trackX[i + 1].node - NODE_R - LINK_GAP
            if (x1 <= x0) return
            g.lineStyle(2, 0x000000, 0.55).moveTo(x0, TRACK_Y).lineTo(x1, TRACK_Y)
            g.lineStyle(1, RIM_LIGHT, 0.14).moveTo(x0, TRACK_Y + 1.5).lineTo(x1, TRACK_Y + 1.5)
            const fill = easeInOutCubic(span(t, from, to))
            if (fill > 0) {
                const head = lerp(x0, x1, fill)
                g.lineStyle({ width: 2, color: REWARD_GOLD, alpha: 1, cap: LINE_CAP.ROUND }).moveTo(x0, TRACK_Y).lineTo(head, TRACK_Y)
                // The bead of light leading the fill, gone once it arrives.
                if (fill < 1) glowDisc(glow, head, TRACK_Y, 7, REWARD_GOLD_HOT, 0.8, 3)
            }
        })
        g.lineStyle(0)
        STEP_LABELS.forEach((_, i) => {
            const cx = this.trackX[i].node, cy = TRACK_Y
            const label = this.trackLabels[i]
            const doneMs = t - STEP_DONE_AT[i]
            if (doneMs >= 0) {
                const stamp = stampPose(doneMs)
                if (stamp.ringT < 1) {
                    g.lineStyle(1.25, REWARD_GOLD, 0.8 * (1 - stamp.ringT))
                    diamond(g, cx, cy, NODE_R + 1 + 11 * easeOutCubic(stamp.ringT))
                    g.lineStyle(0)
                }
                glowDisc(glow, cx, cy, NODE_R * 2.4, REWARD_GOLD, 0.5 * (1 - easeOutCubic(span(doneMs, 0, 600))), 3)
                sparkBurst(glow, cx, cy, stamp.sparkT, 8, NODE_R + 2, NODE_R + 13, REWARD_GOLD, i)
                filledSocket(g, cx, cy, NODE_R * stamp.socketScale, stamp.checkDraw)
                label.tint = lerpColor(RIM_LIGHT, REWARD_GOLD, stamp.labelGold)
                return
            }
            const lit = t >= STEP_LIT_AT[i] ? easeOutCubic(span(t, STEP_LIT_AT[i], STEP_LIT_AT[i] + STEP_LIGHT_MS)) : 0
            if (lit > 0) {
                // The step being shown breathes, gently, so the eye finds it.
                const breathe = 0.5 - 0.5 * Math.cos(((t - STEP_LIT_AT[i]) / 1400) * Math.PI * 2)
                glowDisc(glow, cx, cy, NODE_R * 2.6, REWARD_GOLD, (0.22 + 0.12 * breathe) * lit, 3)
            }
            g.lineStyle({ width: 1.25, color: lerpColor(PLOT_RIM, REWARD_GOLD, lit), alpha: 1, join: LINE_JOIN.MITER })
            g.beginFill(lerpColor(PLOT_FACE, 0x3a2810, lit), 0.95)
            diamond(g, cx, cy, NODE_R)
            g.endFill()
            g.lineStyle(0)
            if (lit > 0) {
                g.beginFill(REWARD_GOLD, 1)
                diamond(g, cx, cy, NODE_R * 0.42 * easeOutBack(span(t, STEP_LIT_AT[i], STEP_LIT_AT[i] + STEP_LIGHT_MS), 2.4))
                g.endFill()
            }
            label.tint = lerpColor(MUTED, INK, lit)
        })
    }

    /** The light over the scene: the wall catching it as it goes up. */
    private drawFx = (t: number) => {
        const g = this.fx.clear()
        // Gold rings where the pointer clicks.
        RIPPLES.forEach(at => {
            const k = span(t, at, at + RIPPLE_MS)
            if (t < at || k >= 1) return
            const p = buildingCursorPose(at, this.cursorStops())
            g.lineStyle(2, REWARD_GOLD, 0.9 * Math.pow(1 - k, 1.5))
            g.drawCircle(p.x, p.y, lerp(3, 16, easeOutCubic(k)))
            g.lineStyle(0)
            glowDisc(g, p.x, p.y, 9, REWARD_GOLD, 0.45 * (1 - k), 3)
        })
        if (t < BUILT_AT) return
        const ms = t - BUILT_AT
        const cx = WALL_X + WALL_W / 2, cy = WALL_Y + WALL_H * 0.6
        // One warm bloom as the row commits, then a band of light across the finished wall.
        const bloom = 1 - easeOutCubic(span(ms, 0, 700))
        if (bloom > 0) glowDisc(g, cx, cy, WALL_W * 0.6, REWARD_GOLD_HOT, 0.6 * bloom, 5)
        // Over the solid planks only: the picket tops have grass between them, which ADD turns lime.
        shineBand(g, WALL_X, WALL_Y + 12, WALL_W, WALL_H - 16, easeInOutCubic(span(ms, 60, 760)), 26, 0.5)
        sparkBurst(g, cx, cy, span(ms, 0, 650), 14, WALL_W * 0.3, WALL_W * 0.75, REWARD_GOLD, 0.4, 1.75)
    }

    private drawStamp = (t: number) => {
        const ms = t - STAMP_AT
        this.stamp.visible = ms >= 0
        this.stampBurst.clear()
        if (ms < 0) return
        const pose = raisedStampPose(ms)
        this.stamp.alpha = pose.alpha
        this.stamp.scale.set(pose.scale)
        const { w, h } = this.stampSize
        const plate = this.stampPlate.clear()
        // Dropped straight down, then the cut-timber plate with a gold rim over a dark casing.
        plate.beginFill(0x000000, 0.4); chamfer(plate, -w / 2, -h / 2 + 3, w, h, 5); plate.endFill()
        plate.lineStyle({ width: 3.5, color: TIMBER_EDGE, alpha: 1, join: LINE_JOIN.MITER })
        plate.beginFill(0x1b1512, 0.97); chamfer(plate, -w / 2, -h / 2, w, h, 5); plate.endFill()
        plate.lineStyle({ width: 1.25, color: REWARD_GOLD, alpha: 1, join: LINE_JOIN.MITER })
        chamfer(plate, -w / 2, -h / 2, w, h, 5)
        plate.lineStyle(1, RIM_LIGHT, 0.25).moveTo(-w / 2 + 6, -h / 2 + 2).lineTo(w / 2 - 6, -h / 2 + 2)
        plate.lineStyle(0)
        filledSocket(plate, -w / 2 + 17, 0, 7, pose.checkDraw)
        const shine = this.stampShine.clear()
        shineBand(shine, -w / 2, -h / 2, w, h, pose.shineT, 22, 0.75)
        sparkBurst(this.stampBurst, 0, 0, pose.burstT, 14, w * 0.42, w * 0.78, REWARD_GOLD, 0.2, 1.6)
    }

    private cursorStops = (): CursorStops => {
        const bar = this.readyBar
        return {
            rest: { x: SCENE_X + 7.2 * CELL, y: SCENE_Y + 3.4 * CELL },
            slot: { x: bar.x + TRAY_PAD + SLOT_PITCH + SLOT * 0.55, y: bar.y + TRAY_PAD + SLOT * 0.5 },
            start: { x: SCENE_X + (WALL_COL + 0.5) * CELL, y: SCENE_Y + (FACE_ROW + 0.5) * CELL },
            end: { x: SCENE_X + (WALL_COL + WALL_SECTIONS - 0.5) * CELL, y: SCENE_Y + (FACE_ROW + 0.5) * CELL },
        }
    }

    /** A chunky arrow with a dark ink edge and a dropped shadow, squashed while it is held down. */
    private drawPointer = (t: number) => {
        const g = this.pointer.clear()
        const pose = buildingCursorPose(t, this.cursorStops())
        if (!(pose.alpha > 0)) return
        const s = pose.pressed ? 0.86 : 1
        const at = (dx: number, dy: number) => cursorArrow.map((v, i) => (i % 2 ? pose.y + dy + v * s : pose.x + dx + v * s))
        g.beginFill(0x000000, 0.35 * pose.alpha).drawPolygon(at(1.5, 2)).endFill()
        g.lineStyle({ width: 1.4, color: 0x1a120c, alpha: pose.alpha, join: LINE_JOIN.ROUND })
        g.beginFill(pose.pressed ? 0xfff0c8 : 0xffffff, pose.alpha).drawPolygon(at(0, 0)).endFill()
        g.lineStyle(0)
    }

    private updateCaption = (pose: ReturnType<typeof buildingTutorialPose>) => {
        const text = pose.done ? DONE_CAPTION : CAPTIONS[pose.step]
        if (this.caption.text !== text) {
            this.caption.text = text
            this.captionShownAt = this.clock
            // Leave room for Replay once it is there to share the row.
            this.caption.style.wordWrapWidth = pose.done ? SCENE_W - REPLAY_W - 10 : SCENE_W
        }
        const k = easeOutCubic(span(this.clock - this.captionShownAt, 0, CAPTION_IN_MS))
        this.caption.alpha = k
        this.caption.y = CAPTION_Y + 5 * (1 - k)
        this.caption.tint = pose.done ? 0xffe2a0 : INK
    }

    destroy = () => {
        this.endDrag()
        if (this.hovered) this.hover(false)
        // First: the feedback destroys its own graphics, which the children sweep would otherwise
        // destroy a second time.
        this.feedback.destroy()
        super.destroy({ children: true })
    }
}
